import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MoreButton from '../MoreButton/MoreButton';
import HeroSectionSkeleton from './HeroSectionSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { IMAGES_BASE_URL } from '../../config/constants';
import './HeroSection.scss';

const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

const processProjectData = (project) => {
  if (!project) return null;

  const titleText = typeof project.mainTitle === 'object'
    ? project.mainTitle.en || Object.values(project.mainTitle)[0]
    : project.mainTitle;

  const slug = generateSlug(titleText);

  return {
    ...project,
    button: {
      text: {
        ua: 'Переглянути проект',
        en: 'View Project',
        ru: 'Посмотреть проект',
        pl: 'Zobacz projekt',
        de: 'Projekt ansehen',
        es: 'Ver proyecto',
      },
      link: `/work/${slug}`,
    }
  };
};

const HeroSection = ({ page = 'HomePage', showRecentWork = true, showMoreButton = true }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const {
    data: mainData,
    loading: mainLoading,
    error: mainError,
    refetch: refetchMainData
  } = useCachedFetch('/data/locales/mainData.json');

  const {
    data: workData,
    loading: workLoading,
    error: workError,
    refetch: refetchWorkData
  } = useCachedFetch('/data/locales/work.json');

  const handleRetry = useCallback(() => {
    refetchMainData();
    refetchWorkData();
  }, [refetchMainData, refetchWorkData]);

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [page]);

  useEffect(() => {
    if (mainLoading || workLoading) return;

    if (mainError || workError) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const processedData = (workData?.WorkData || []).map(project => processProjectData(project));

    const mainContent = mainData?.TopMainData?.[page];
    const recentProj = processedData[processedData.length - 1];

    let dataIsValid = mainContent &&
      mainContent.contentAccent &&
      mainContent.contentTitle &&
      mainContent.contentSubtitle &&
      mainContent.button &&
      mainContent.button.link &&
      mainContent.button.text;

    if (page === 'HomePage') {
      dataIsValid = dataIsValid &&
        mainContent.allWork &&
        mainContent.status &&
        mainContent.iconAllWork &&
        recentProj &&
        recentProj.mainTitle &&
        recentProj.bgImage &&
        recentProj.accent;
    } else {
      dataIsValid = dataIsValid && mainContent.bgImage;
    }

    if (dataIsValid) {
      setHasError(false);
    } else {
      setHasError(true);
    }

    setIsLoading(false);
  }, [mainLoading, workLoading, mainError, workError, mainData, workData, page]);

  const heroData = useMemo(() => {
    if (!mainData || !workData) return null;

    const processedData = (workData.WorkData || []).map(project => processProjectData(project));
    const mainContent = mainData.TopMainData?.[page];
    const recentProj = processedData[processedData.length - 1];

    if (!mainContent) return null;

    return {
      mainContent,
      recentProject: page === 'HomePage' ? recentProj : null,
      projectSlug: page === 'HomePage' ? generateSlug(recentProj?.mainTitle) : '',
      projectAccent: page === 'HomePage' ? (recentProj?.accent?.[currentLang] || recentProj?.accent?.en || '') : ''
    };
  }, [mainData, workData, page, currentLang]);

  const getTranslation = useCallback((textObj) => {
    if (!textObj) return '';
    return textObj[currentLang] || textObj.en || '';
  }, [currentLang]);

  const mainTranslations = useMemo(() => {
    if (!heroData) return {};

    const { mainContent } = heroData;
    return {
      accent: getTranslation(mainContent.contentAccent),
      title: getTranslation(mainContent.contentTitle),
      subtitle: getTranslation(mainContent.contentSubtitle),
      buttonText: getTranslation(mainContent.button?.text),
      allWork: getTranslation(mainContent.allWork),
      status: getTranslation(mainContent.status)
    };
  }, [heroData, getTranslation]);

  const shouldShowRecentWork = useMemo(() => {
    const result = page === 'HomePage' && showRecentWork && heroData?.recentProject;
    return result;
  }, [page, showRecentWork, heroData]);

  const shouldShowBackgroundImage = useMemo(() => {
    const result = page !== 'HomePage' && heroData?.mainContent?.bgImage;
    return result;
  }, [page, heroData]);

  const backgroundImagePages = useMemo(() => [
    'AboutPage', 'B2BPage', 'B2CPage', 'BrandingPage', 'CROPage', 'eCommercePage',
    'GraphicDesignPage', 'LogoDesignPage', 'MarketingPage', 'SEOPage', 'ServicesPage',
    'UxDesignPage', 'WebDesignPage', 'WebDevPage', 'crafted-creativity', 'growth-strategy',
    'digital-advertising', 'content-promotion', 'sales-automation', 'data-insights'
  ], []);

  const isBackgroundImagePage = useMemo(() => {
    const result = backgroundImagePages.includes(page);
    return result;
  }, [backgroundImagePages, page]);

  if (isLoading) {
    return <HeroSectionSkeleton page={page} showRecentWork={showRecentWork} showMoreButton={showMoreButton} />;
  }

  if (hasError || !heroData) {
    return <ErrorState onRetry={handleRetry} />;
  }

  const { mainContent, recentProject, projectSlug, projectAccent } = heroData;

  return (
    <div className='hero__section'>
      <div className='left__side__hero__section'>
        <div className='hero__content'>
          <p className='hero__accent'>
            {mainTranslations.accent}
          </p>
          <h1 className='hero__title'>
            {mainTranslations.title}
          </h1>
          <h2 className='hero__subtitle'>
            {mainTranslations.subtitle}
          </h2>
          <Link to={mainContent.button.link} className='hero__button'>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            {mainTranslations.buttonText}
          </Link>
        </div>
        {showMoreButton && <MoreButton />}
      </div>

      <div className='right__side__hero__section__wrapper'>
        <div className='hero__animation'></div>
        <div className='right__side__hero__section'>

          {shouldShowRecentWork && (
            <>
              <div className='all__work'>
                <Link to={mainContent.button.link} className='all__work__text'>
                  {mainTranslations.allWork}
                  <img src={`${IMAGES_BASE_URL}${mainContent.iconAllWork}`} alt="All-Work-Icon" className="all__work__icon" loading="lazy" />
                </Link>
              </div>
              <div className='background__substrate'>
                <div className='recent__work' style={{ backgroundImage: `url(${IMAGES_BASE_URL}${recentProject.bgImage})` }}>
                  <div className='recent__work__substrate__gradient'></div>
                  <div className='recent__work__content'>
                    <p className='recent__work__accent'>
                      {mainTranslations.status}
                    </p>
                    <Link to={`/work/${projectSlug}`} className='recent__work__title'>
                      {recentProject.mainTitle}
                    </Link>
                    <h3 className='recent__work__subtitle'>{projectAccent}</h3>
                    <Link to={`/work/${projectSlug}`} className='recent__work__button'>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      {getTranslation(recentProject.button?.text) || 'View Project'}
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )}

          {shouldShowBackgroundImage && isBackgroundImagePage && (
            <div className='background__substrate'>
              <div className='recent__work' style={{ backgroundImage: `url(${IMAGES_BASE_URL}${mainContent.bgImage})` }}>
                <div className='recent__work__substrate__gradient'></div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroSection);